/* Il promemoria delle ore dimenticate: i giorni feriali senza ore delle ultime due settimane.
 *
 * Dal 16 ottobre 2026. La striscia della settimana segna gia' "vuoto" un feriale passato, ma
 * solo nella settimana che si sta guardando: un martedi' dimenticato di dieci giorni fa non lo
 * vede nessuno. Qui si guarda indietro di 14 giorni e si elencano i giorni da aprire, uno per
 * clic.
 *
 * **Non e' un controllo, e non giudica.** Le ferie, un permesso, un giorno di festa non sono
 * dimenticanze: per questo si puo' dire "non ho lavorato" e quei giorni smettono di comparire.
 * La scelta sta solo nel browser di chi la fa (`localStorage`): e' una comodita', non un dato
 * dello studio, e non arriva a nessun altro (chi dirige non la vede).
 *
 * **Se nelle due settimane non c'e' nemmeno un'ora il promemoria tace**: una persona nuova o in
 * ferie lunghe non ha dimenticato dieci giorni, e dieci chip rossi la scoraggerebbero.
 */

import { useState } from "react";
import { ore } from "../api";
import { useCarica } from "../carica";
import { giornoLeggibile, oggi, type Imputazione } from "../dominio";
import { aggiungiGiorni } from "./Settimana";

const GIORNI_INDIETRO = 14;

function eFeriale(iso: string): boolean {
  const giorno = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return giorno >= 1 && giorno <= 5;
}

function chiave(persona: string): string {
  return `ore-nascoste-${persona}`;
}

function lette(persona: string): string[] {
  try {
    const grezzo = window.localStorage.getItem(chiave(persona));
    const letto: unknown = grezzo ? JSON.parse(grezzo) : [];
    return Array.isArray(letto) ? letto.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** I giorni feriali passati, nella finestra, senza nemmeno un'imputazione. Pura, per provarla. */
export function giorniSenzaOre(
  imputazioni: Pick<Imputazione, "giorno">[],
  dal: string,
  al: string,
): string[] {
  if (imputazioni.length === 0) return [];
  const conOre = new Set(imputazioni.map((i) => i.giorno));
  const fuori: string[] = [];
  for (let g = dal; g <= al; g = aggiungiGiorni(g, 1)) {
    if (eFeriale(g) && !conOre.has(g)) fuori.push(g);
  }
  return fuori;
}

export function OreDimenticate({
  persona,
  versione,
  scegli,
}: {
  persona: string;
  /** Cambia quando si salva un'ora: il promemoria si rilegge. */
  versione: number;
  scegli: (giorno: string) => void;
}) {
  const al = aggiungiGiorni(oggi(), -1);
  const dal = aggiungiGiorni(oggi(), -GIORNI_INDIETRO);
  const dati = useCarica<Imputazione[]>(() => ore.mie(dal, al), [dal, versione]);
  const [nascosti, setNascosti] = useState<string[]>(() => lette(persona));

  const mancanti = giorniSenzaOre(dati.dati ?? [], dal, al).filter((g) => !nascosti.includes(g));
  if (mancanti.length === 0) return null;

  function nascondi() {
    const tutti = [...nascosti, ...mancanti];
    setNascosti(tutti);
    try {
      window.localStorage.setItem(chiave(persona), JSON.stringify(tutti));
    } catch {
      // Senza memoria del browser il promemoria ricompare al prossimo giro: nessun danno.
    }
  }

  return (
    <section className="ore-mancanti" role="status" aria-label="Giorni senza ore">
      <p>
        <strong>Mancano le ore di:</strong>{" "}
        {mancanti.map((g) => (
          <button key={g} type="button" className="tasto tasto-piccolo" onClick={() => scegli(g)}>
            {giornoLeggibile(g)}
          </button>
        ))}
      </p>
      <button type="button" className="tasto tasto-nudo" onClick={nascondi}>
        Non ho lavorato in questi giorni
      </button>
    </section>
  );
}
